/**
 * @packageDocumentation ManageLinkedCounterpartsModal
 *
 * Renders modal that allow the user to manage linked counterparts. The modal is composed of two steps:
 *  - selection: this is where user can select the counterparts so merge/associate
 *  - choose_main: this is where user can selecte the main/parent counterpart from the counterparts selected on step 1
 * 
 * If an existing main counterpart is provided, we dont show already selected children and we skip the 'choose_main' step.
 *
 * Example
 * ```tsx
 * import CounterpartChildrenAssociationModal from '../counterparts/CounterpartChildrenAssociationModal';
 *
 * ...
 *
 * showModal({
 *    component: CounterpartChildrenAssociationModal,
 *    props: { 
        mainCounterpart: counterpart,
        searchText: 'POI',
      }
 *  })
 * ```
 *
 * @category Component
 */

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import Modal from '~/components/modals/Modal/Modal';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import { snack } from '~/hooks/ToastProvider';
import { useAnalytics } from '~/hooks/useAnalytics';
import useDataTestId from '~/hooks/useDataTestId';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconLinkOn from '~/icons/icon-link-on.svg?react';
import IconSearch from '~/icons/icon-search.svg?react';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useBulkUpdateCounterpartsParent, useCounterparts } from '~/services/counterparts';
import colors from '~/theme/colors.module.scss';
import { IdentityType } from '~/types/company-identity';
import { Counterpart, CounterpartWithChildren } from '~/types/counterparts';
import { assertIsDefined } from '~/types/typeGuards';
import { isNotEmpty } from '~/utils/helpers';
import { extractAxiosJSONApiErrors } from '~/utils/jsonapi-errors';
import styles from './CounterpartChildrenAssociationModal.module.scss';
import CounterpartSummaryRow, { CounterpartName, useCounterpartDetails } from './CounterpartSummaryRow';
import PageEndMarker from '../PageEndMarker';
import Button from '../buttons/NewButton';
import AlertMessage from '../data-display/AlertMessage';
import Chip from '../data-display/Chip/Chip';
import Checkbox from '../inputs/Checkbox/Checkbox';
import Radio from '../inputs/Radio/Radio';
import RadioGroup from '../inputs/RadioGroup/RadioGroup';
import TextField from '../inputs/TextField/TextField';
import { HStack, VStack } from '../layout/Stack';
import ModalActions from '../modals/Modal/ModalActions';
import Text from '../typographies/Text';

/**
 * The props type for {@link CounterpartChildrenAssociationModal}.
 * Extends {@link ModalProps}.
 *
 * @interface
 * @category Props
 */
export interface CounterpartChildrenAssociationModalProps extends ModalProps {
  /** parent counterpart, if it is defined we will skip "choose_main" step */
  mainCounterpart?: CounterpartWithChildren;

  /** initial value for counterpart search. */
  searchText?: string;

  /** if true the search field is not displayed */
  disableSearch?: boolean;

  /** string to propagate data-testid attribute value */
  dataTestId?: string;
}

const useIdentityTypeTranslation = (counterpart: Counterpart) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'identities' });

  switch (counterpart.identityType) {
    case IdentityType.Company:
      return t('company');
    case IdentityType.Pa:
      return t('PA');
    case IdentityType.Contractor:
      return t('contractor');
    case IdentityType.Person:
      return t('person');
    default:
      return '';
  }
};

const CounterpartSelectionCard: React.FC<{ counterpart: CounterpartWithChildren; dataTestId?: string }> = ({
  counterpart,
  dataTestId,
}) => {
  const dt = useDataTestId(dataTestId);
  const { t } = useTranslation('counterparts', { keyPrefix: 'children_association_modal.counterpart_card' });
  const identityTypeTranslation = useIdentityTypeTranslation(counterpart);
  return (
    <Radio value={counterpart.id} className={styles.counterpartCardRadio} dataTestId={dt('radio')}>
      <VStack gap={1}>
        <Text variant="body2" weight="semibold">
          {counterpart.companyName}
        </Text>
        <VStack gap={0.5}>
          <Text variant="caption" weight="medium" secondaryColor>
            {t('identity_type_label', { value: identityTypeTranslation })}
          </Text>
          <Text variant="caption" weight="medium" secondaryColor>
            {t('address_label', { value: counterpart.address })}
          </Text>
          <Text variant="caption" weight="medium" secondaryColor>
            {t('tax_number_label', { value: counterpart.taxNumber })}
          </Text>
          <Text variant="caption" weight="medium" secondaryColor>
            {t('vat_number_label', { value: counterpart.vatNumber })}
          </Text>
          <Text variant="caption" weight="medium" secondaryColor>
            {t('recipient_code_label', { value: counterpart.destinationCode })}
          </Text>
        </VStack>
        {isNotEmpty(counterpart.children) ? (
          <div>
            <Chip
              size="small"
              background={colors.colorViolet200}
              label={t('associated_counterparts', { count: counterpart.children.length })}
              className={styles.counterpartCardChip}
            />
          </div>
        ) : null}
      </VStack>
    </Radio>
  );
};

const ChooseMainCounterpartContent: React.FC<{
  counterpartIds: string[];
  initialMainCounterpart?: string;
  onBack: () => void;
  onSubmit: (counterpartId: string) => void;
  isSubmitting?: boolean;
  dataTestId?: string;
}> = ({ counterpartIds, initialMainCounterpart, onSubmit, onBack, isSubmitting, dataTestId }) => {
  const dt = useDataTestId(dataTestId);
  const { t } = useTranslation('counterparts', { keyPrefix: 'children_association_modal' });
  const query = useCounterparts({ ids: counterpartIds });
  const counterparts = query.data?.pages.flatMap((p) => p.counterparts) ?? [];
  const [selectedCounterpart, setSelectedCounterparts] = useState<string | undefined>(initialMainCounterpart);

  const handleSubmit = () => {
    assertIsDefined(selectedCounterpart, 'a counterpart must be selected');
    onSubmit(selectedCounterpart);
  };

  return (
    <>
      <ModalContent className={styles.content}>
        <Text variant="body2" weight="regular">
          {t('choose_main_subtitle')}
        </Text>
        <RadioGroup
          onChange={setSelectedCounterparts}
          value={selectedCounterpart}
          ariaLabel="main counterpart"
          className={styles.list}
        >
          <VStack gap={2}>
            <div className={styles.counterpartCardsRow}>
              {counterparts.map((c) => (
                <CounterpartSelectionCard key={c.id} counterpart={c} dataTestId={dt(c.id, 'card')} />
              ))}
            </div>
            <PageEndMarker height={72} query={query} />
          </VStack>
        </RadioGroup>
        {selectedCounterpart && (
          <AlertMessage box type="warning">
            <div>
              <Trans t={t} i18nKey="choose_main_warning" components={{ bold: <strong /> }} />
            </div>
          </AlertMessage>
        )}
      </ModalContent>
      <ModalActions>
        <Button onClick={onBack} variant="outlined" disabled={isSubmitting} data-testid={dt('backButton')}>
          {t('cancel_button')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isNil(selectedCounterpart)}
          loading={isSubmitting}
          data-testid={dt('submitButton')}
        >
          {t('submit_button')}
        </Button>
      </ModalActions>
    </>
  );
};

const MainCounterpart: React.FC<{ counterpart: Counterpart }> = ({ counterpart }) => {
  const details = useCounterpartDetails(counterpart);
  const { t } = useTranslation('counterparts', { keyPrefix: 'children_association_modal' });

  return (
    <VStack gap={0.5} className={styles.mainCounterpartRow}>
      <CounterpartName counterpart={counterpart} />
      <Text variant="caption" weight="medium" style={{ color: colors.colorGrey700 }}>
        {details} • {t('email')}
        {counterpart.contactEmail ?? '-'}
      </Text>
    </VStack>
  );
};

const SelectDuplicatedCounterpartsContent: React.FC<
  Omit<CounterpartChildrenAssociationModalProps, 'closeModal' | 'mainCounterpart'> & {
    onSubmit: (values: string[]) => void;
    initialValues: string[];
    mainCounterpart?: CounterpartWithChildren;
    isSubmitting?: boolean;
  }
> = ({
  dataTestId,
  searchText: initialSearchText,
  onSubmit,
  initialValues,
  disableSearch,
  mainCounterpart,
  isSubmitting,
}) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'children_association_modal' });
  const dt = useDataTestId(dataTestId);
  const [searchText, setSearchText] = useState(initialSearchText ?? '');
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const [selectedCounterparts, setSelectedCounterparts] = useState<string[]>(initialValues);

  const query = useCounterparts({
    search: debouncedSearchText,
    excludeIds: mainCounterpart ? [mainCounterpart.id, ...mainCounterpart.children.map((el) => el.id)] : [],
  });
  const counterpartOptions = query.data?.pages.flatMap((p) => p.counterparts) ?? [];

  return (
    <>
      <ModalContent className={styles.content} data-testid={dt('content')}>
        <Text variant="body2" weight="regular">
          {t('select_subtitle')}
        </Text>
        {mainCounterpart && <MainCounterpart counterpart={mainCounterpart} />}
        {!disableSearch && (
          <TextField
            label={t('search_label')}
            hideLabel
            placeholder={t('search_label')}
            value={searchText}
            Icon={IconSearch}
            onChange={(val) => setSearchText(val)}
            dataTestId={dt('searchField')}
          />
        )}
        <VStack gap={1} className={styles.list}>
          {counterpartOptions.map((child) => {
            const selected = selectedCounterparts.includes(child.id);
            return (
              <HStack align="center" gap={3} key={child.id} className={styles.counterpartRow} data-selected={selected}>
                <Checkbox
                  checked={selected}
                  dataTestId={dt(child.id, 'checkbox')}
                  onChange={(val) => {
                    setSelectedCounterparts((records) => {
                      if (val) return records.concat([child.id]);
                      return records.filter((el) => el !== child.id);
                    });
                  }}
                />
                <CounterpartSummaryRow counterpart={child} style={{ flex: 1 }} />
              </HStack>
            );
          })}
          <PageEndMarker height={72} query={query} />
        </VStack>
        <AlertMessage box type="info" size="medium" className={styles.selectionWarning}>
          {t('selection_warning')}
        </AlertMessage>
      </ModalContent>
      <ModalActions>
        <Button
          onClick={() => onSubmit(selectedCounterparts)}
          disabled={isEmpty(selectedCounterparts)}
          loading={isSubmitting}
          data-testid={dt('submitButton')}
        >
          {t('submit_button')}
        </Button>
      </ModalActions>
    </>
  );
};

const CounterpartChildrenAssociationModal = ({
  mainCounterpart: initialMainCounterpart,
  closeModal,
  dataTestId,
  searchText: initialSearchText,
  disableSearch,
}: CounterpartChildrenAssociationModalProps) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'children_association_modal' });
  const dt = useDataTestId(dataTestId);
  const { captureException } = useExceptionLogger();
  const { track } = useAnalytics();
  const { mutateAsync: bulkUpdateCounterpartsParent, isPending } = useBulkUpdateCounterpartsParent();
  const [step, setStep] = useState<'selection' | 'choose_main'>('selection');
  const [selectedCounterparts, setSelectedCounterparts] = useState<string[]>([]);
  useEffect(() => {
    track(AnalyticsEvents.MERGE_COUNTERPARTS_MODAL_OPENED, {
      parentId: initialMainCounterpart?.id,
      childrenIds: initialMainCounterpart?.children.map((el) => el.id),
    });
  }, [initialMainCounterpart?.children, initialMainCounterpart?.id, track]);

  const handleAssociateCounterparts = async (parent: string, childrenIds: string[]) => {
    try {
      await bulkUpdateCounterpartsParent({
        counterpartsIds: childrenIds,
        parentId: parent,
      });
      snack.success(t('success_message'));
      track(AnalyticsEvents.COUNTERPARTS_MERGED, {
        parentId: parent,
        childrenIds,
      });
    } catch (err) {
      captureException(err, {
        extra: {
          parent_id: parent,
          children_ids: childrenIds,
          help_text: 'associating children to counterpart',
        },
      });
      const apiError = extractAxiosJSONApiErrors(err);
      snack.error(apiError?.at(0)?.detail || t('error_message'));
    } finally {
      closeModal();
    }
  };

  const handleSelectionSubmit = (values: string[]) => {
    if (initialMainCounterpart) return handleAssociateCounterparts(initialMainCounterpart.id, values);

    setStep('choose_main');
    setSelectedCounterparts(values);
  };

  const handleSelectedMainCounterpartSubmit = (counterpartId: string) => {
    const children = selectedCounterparts.filter((el) => el !== counterpartId);
    return handleAssociateCounterparts(counterpartId, children);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal();
    }
  };

  return (
    <Modal className={styles.modal} isOpen maxWidth={640} onOpenChange={handleOpenChange}>
      <ModalTitle Icon={IconLinkOn} dataTestId={dt('title')} onCloseClick={closeModal}>
        {t('title')}
      </ModalTitle>
      {step === 'selection' ? (
        <SelectDuplicatedCounterpartsContent
          dataTestId={dt('selectionStep')}
          initialValues={selectedCounterparts}
          disableSearch={disableSearch}
          searchText={initialSearchText}
          onSubmit={handleSelectionSubmit}
          mainCounterpart={initialMainCounterpart}
          isSubmitting={isPending}
        />
      ) : (
        <ChooseMainCounterpartContent
          counterpartIds={selectedCounterparts}
          dataTestId={dt('chooseMainStep')}
          initialMainCounterpart={initialMainCounterpart?.id}
          onBack={() => setStep('selection')}
          onSubmit={handleSelectedMainCounterpartSubmit}
          isSubmitting={isPending}
        />
      )}
    </Modal>
  );
};

export default CounterpartChildrenAssociationModal;
